import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { ClockIcon, DocumentTextIcon, LinkIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import { AdaptiveModal } from './AdaptiveModal';
import { Event, EventCreate, EventUpdate, Todo } from '../types';
import { useEvents } from '../hooks/useEvents';
import { useTodos } from '../hooks/useTodos';
import { useNotification } from '../hooks/useNotification';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event?: Event | null;
  initialStartTime?: Date;
  initialEndTime?: Date;
  linkedTodo?: Todo | null;
  onLinkedTodoClick?: (todo: Todo) => void;
}

export const EventModal: React.FC<EventModalProps> = ({
  isOpen,
  onClose,
  event,
  initialStartTime,
  initialEndTime,
  linkedTodo,
  onLinkedTodoClick,
}) => {
  const { createEvent, updateEvent, deleteEvent } = useEvents();
  const { todos } = useTodos();
  const { showNotification } = useNotification();
  
  // Get the linked todo if this event has one
  const linkedTodoData = event?.todo_id ? todos.find(t => t.id === event.todo_id) : linkedTodo;
  const isFromGoogle = event?.is_from_google ?? false;
  
  const [formData, setFormData] = useState<EventCreate | EventUpdate>({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    color: '#3B82F6',
    todo_id: undefined,
  });
  
  const [selectedDuration, setSelectedDuration] = useState<number>(60); // Duration in minutes

  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasInitialized = useRef(false);
  const isUpdatingDuration = useRef(false);

  // Helper function to format date for datetime-local input (local timezone)
  const formatForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper function to parse datetime-local input to local Date
  const parseFromInput = (dateTimeString: string) => {
    // datetime-local gives us a string like "2025-07-08T14:00"
    // We want to interpret this as local time, not UTC
    const [datePart, timePart] = dateTimeString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    
    // Create date in local timezone
    return new Date(year, month - 1, day, hours, minutes);
  };
  
  // Generate duration options: 5min increments up to 1hr, then 15min up to 3hrs
  const getDurationOptions = () => {
    const options = [];
    
    // 5-minute increments up to 1 hour
    for (let i = 5; i <= 60; i += 5) {
      const hours = Math.floor(i / 60);
      const minutes = i % 60;
      const label = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
      options.push({ value: i, label });
    }
    
    // 15-minute increments from 1h 15m to 3 hours
    for (let i = 75; i <= 180; i += 15) {
      const hours = Math.floor(i / 60);
      const minutes = i % 60;
      const label = minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
      options.push({ value: i, label });
    }
    
    return options;
  };
  
  const durationOptions = getDurationOptions();
  
  // Calculate duration from start and end times
  const calculateDuration = useCallback((startTime: string, endTime: string) => {
    if (!startTime || !endTime) return 60;
    
    const start = parseFromInput(startTime);
    const end = parseFromInput(endTime);
    const durationMs = end.getTime() - start.getTime();
    return Math.max(5, Math.round(durationMs / (1000 * 60))); // Minimum 5 minutes
  }, []);
  
  // Update end time based on duration
  const updateEndTimeFromDuration = (startTime: string, durationMinutes: number) => {
    if (!startTime) return '';
    
    const start = parseFromInput(startTime);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
    return formatForInput(end);
  };


  // Initialize form data when modal opens
  useEffect(() => {
    if (!isOpen) {
      hasInitialized.current = false;
      return;
    }

    if (hasInitialized.current) return; // Don't reinitialize if already done

    let initialValues;
    if (event) {
      // Editing existing event
      initialValues = {
        title: event.title,
        description: event.description || '',
        start_time: formatForInput(parseISO(event.start_time)),
        end_time: formatForInput(parseISO(event.end_time)),
        color: event.color || '#3B82F6',
        todo_id: event.todo_id,
      };
    } else if (linkedTodo) {
      // Creating event from todo
      const startTime = initialStartTime || new Date();
      const endTime = initialEndTime || new Date(startTime.getTime() + 60 * 60 * 1000);
      
      initialValues = {
        title: linkedTodo.title,
        description: linkedTodo.description || '',
        start_time: formatForInput(startTime),
        end_time: formatForInput(endTime),
        color: '#10B981',
        todo_id: linkedTodo.id,
      };
    } else {
      // Creating new event
      const startTime = initialStartTime || new Date();
      const endTime = initialEndTime || new Date(startTime.getTime() + 60 * 60 * 1000);

      initialValues = {
        title: '',
        description: '',
        start_time: formatForInput(startTime),
        end_time: formatForInput(endTime),
        color: '#3B82F6',
        todo_id: undefined,
      };
    }

    setFormData(initialValues);
    
    // Set initial duration
    if (initialValues.start_time && initialValues.end_time) {
      const duration = calculateDuration(initialValues.start_time, initialValues.end_time);
      setSelectedDuration(duration);
    }
    
    hasInitialized.current = true;
  }, [isOpen, event, linkedTodo, initialStartTime, initialEndTime]);
  
  // Update duration when start or end time changes (but not when duration is being set)
  useEffect(() => {
    if (!isUpdatingDuration.current && formData.start_time && formData.end_time) {
      const duration = calculateDuration(formData.start_time, formData.end_time);
      setSelectedDuration(duration);
    }
  }, [formData.start_time, formData.end_time, calculateDuration]);
  
  // Handle duration change
  const handleDurationChange = (durationMinutes: number) => {
    isUpdatingDuration.current = true;
    setSelectedDuration(durationMinutes);
    
    if (formData.start_time) {
      const newEndTime = updateEndTimeFromDuration(formData.start_time, durationMinutes);
      setFormData(prev => ({ ...prev, end_time: newEndTime }));
    }
    
    // Reset the flag after a brief delay
    setTimeout(() => {
      isUpdatingDuration.current = false;
    }, 100);
  };
  
  // Handle start time change - update end time to maintain duration
  const handleStartTimeChange = (newStartTime: string) => {
    isUpdatingDuration.current = true;
    setFormData(prev => ({ 
      ...prev, 
      start_time: newStartTime,
      end_time: newStartTime ? updateEndTimeFromDuration(newStartTime, selectedDuration) : ''
    }));
    
    setTimeout(() => {
      isUpdatingDuration.current = false;
    }, 100);
  };

  const saveEvent = async (silent = false) => {
    if (!formData.title?.trim()) {
      if (!silent) showNotification('Please enter a title', 'error');
      return false;
    }

    if (!formData.start_time || !formData.end_time) {
      if (!silent) showNotification('Please select start and end times', 'error');
      return false;
    }

    // Parse datetime-local input values as local time
    // The input format is "2025-07-08T14:00" which should be interpreted as local time
    const startTime = parseFromInput(formData.start_time);
    const endTime = parseFromInput(formData.end_time);

    if (endTime <= startTime) {
      if (!silent) showNotification('End time must be after start time', 'error');
      return false;
    }

    setIsSubmitting(true);

    try {
      // Format times for API - use local time format without timezone conversion
      const formatForAPI = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const eventData: EventCreate = {
        title: formData.title!.trim(),
        description: formData.description?.trim(),
        start_time: formatForAPI(startTime),
        end_time: formatForAPI(endTime),
        color: formData.color,
        todo_id: formData.todo_id,
      };

      if (event) {
        await updateEvent({ eventId: event.id, event: eventData });
        if (!silent) showNotification('Event updated successfully', 'success');
      } else {
        await createEvent(eventData);
        if (!silent) showNotification('Event created successfully', 'success');
      }
      
      return true;
    } catch (error) {
      console.error('Save event error:', error);
      if (!silent) showNotification('Failed to save event', 'error');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await saveEvent();
    if (success) {
      onClose();
    }
  };

  const handleClose = () => {
    // Fire-and-forget auto-save if there's content (but never for Google Calendar events).
    // Don't await — close immediately so the modal isn't blocked by network issues.
    if (!isFromGoogle && formData.title?.trim() && formData.start_time && formData.end_time) {
      saveEvent(true);
    }
    onClose();
  };

  const handleColorChange = (color: string) => {
    setFormData(prev => ({ ...prev, color }));
  };

  const colorOptions = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Yellow
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#6B7280', // Gray
    '#059669', // Emerald
  ];

  // Read-only view for Google Calendar events
  if (isFromGoogle && event) {
    const startDate = parseISO(event.start_time);
    const endDate = parseISO(event.end_time);

    return (
      <AdaptiveModal isOpen={isOpen} onClose={onClose} title="Google Calendar Event">
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">From Google Calendar</p>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{event.title}</h3>
          </div>

          {event.description && (
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                <DocumentTextIcon className="inline h-4 w-4 mr-1" />
                Description
              </label>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{event.description}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              <ClockIcon className="inline h-4 w-4 mr-1" />
              Time
            </label>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {format(startDate, 'EEE, MMM d, yyyy h:mm a')} &mdash; {format(endDate, 'h:mm a')}
            </p>
          </div>

          {event.google_html_link && (
            <a
              href={event.google_html_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
            >
              <LinkIcon className="h-4 w-4" />
              Open in Google Calendar
            </a>
          )}

          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
        </div>
      </AdaptiveModal>
    );
  }

  return (
    <AdaptiveModal isOpen={isOpen} onClose={handleClose} title={event ? 'Edit Event' : 'Schedule Task'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Task Selection - Most Prominent */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="todo_id" className="block text-lg font-semibold text-gray-900">
              <LinkIcon className="inline h-5 w-5 mr-2 text-blue-600" />
              Select Task to Schedule
            </label>
            {linkedTodoData && onLinkedTodoClick && (
              <button
                type="button"
                onClick={() => onLinkedTodoClick(linkedTodoData)}
                className="flex items-center gap-2 px-3 py-1 text-sm font-medium text-blue-600 bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                title="View linked task details"
              >
                <EyeIcon className="h-4 w-4" />
                View Task
              </button>
            )}
          </div>
          <select
            id="todo_id"
            value={formData.todo_id || ''}
            onChange={(e) => {
              const selectedTodoId = e.target.value || undefined;
              const selectedTodo = todos.find(t => t.id === selectedTodoId);

              setFormData(prev => ({
                ...prev,
                todo_id: selectedTodoId,
                // Auto-populate title if it's empty or was auto-populated from another task
                title: selectedTodo ? selectedTodo.title : (prev.title || ''),
                color: selectedTodo ? '#10B981' : '#3B82F6' // Green for task-linked events
              }));
            }}
            className="mt-1 block w-full text-lg rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Create event without task</option>
            {todos
              .filter(todo => todo.status !== 'completed')
              .sort((a, b) => {
                // Sort by due date, then by priority, then by title
                if (a.due_date && b.due_date) {
                  return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
                }
                if (a.due_date && !b.due_date) return -1;
                if (!a.due_date && b.due_date) return 1;

                const priorityOrder = { high: 3, medium: 2, low: 1 };
                const aPriority = priorityOrder[a.priority || 'medium'] || 2;
                const bPriority = priorityOrder[b.priority || 'medium'] || 2;

                if (aPriority !== bPriority) return bPriority - aPriority;
                return a.title.localeCompare(b.title);
              })
              .map((todo) => {
                const dueText = todo.due_date ? ` (due ${format(parseISO(todo.due_date), 'MMM d')})` : '';
                const priorityText = todo.priority === 'high' ? ' 🔴' : todo.priority === 'low' ? ' 🟡' : '';
                return (
                  <option key={todo.id} value={todo.id}>
                    {todo.title}{dueText}{priorityText}
                  </option>
                );
              })}
          </select>
        </div>

        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Event Title {formData.todo_id && '(from selected task)'}
          </label>
          <input
            type="text"
            id="title"
            value={formData.title || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder={formData.todo_id ? "Title will be set from task" : "Event title"}
            required
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            <DocumentTextIcon className="inline h-4 w-4 mr-1" />
            Description
          </label>
          <textarea
            id="description"
            value={formData.description || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="Optional description"
          />
        </div>

        {/* Time Selection */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label htmlFor="start_time" className="block text-sm font-medium text-gray-700">
              <ClockIcon className="inline h-4 w-4 mr-1" />
              Start Time
            </label>
            <input
              type="datetime-local"
              id="start_time"
              value={formData.start_time || ''}
              onChange={(e) => handleStartTimeChange(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              required
            />
          </div>

          <div>
            <label htmlFor="end_time" className="block text-sm font-medium text-gray-700">
              <ClockIcon className="inline h-4 w-4 mr-1" />
              End Time
            </label>
            <input
              type="datetime-local"
              id="end_time"
              value={formData.end_time || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              required
            />
          </div>

          <div>
            <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
              <ClockIcon className="inline h-4 w-4 mr-1" />
              Duration
            </label>
            <select
              id="duration"
              value={selectedDuration}
              onChange={(e) => handleDurationChange(Number(e.target.value))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {durationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>


        {/* Color Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Color
          </label>
          <div className="flex space-x-2">
            {colorOptions.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => handleColorChange(color)}
                className={`w-8 h-8 rounded-full border-2 ${
                  formData.color === color ? 'border-gray-900' : 'border-gray-300'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>


        {/* Form Actions */}
        <div className="flex justify-between pt-4">
          {/* Delete button - only show when editing existing event */}
          {event && (
            <button
              type="button"
              onClick={async () => {
                if (window.confirm('Are you sure you want to delete this event?')) {
                  try {
                    await deleteEvent(event.id);
                    showNotification('Event deleted successfully', 'success');
                    onClose();
                  } catch (error) {
                    console.error('Failed to delete event:', error);
                    showNotification('Failed to delete event', 'error');
                  }
                }
              }}
              disabled={isSubmitting}
              className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <TrashIcon className="h-4 w-4" />
              <span>Delete Event</span>
            </button>
          )}

          {/* Cancel and Save buttons */}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Saving...' : event ? 'Update Event' : 'Create Event'}
            </button>
          </div>
        </div>
      </form>
    </AdaptiveModal>
  );
};